import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamSheetsFilteredByTableQuery,
  DatabaseDownstreamSheetsFilteredByTableQuery_tables_downstreamSheetsConnection_nodes,
  DatabaseDownstreamSheetsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamSheetsFilteredByTableQuery';
import {
  DatabaseDownstreamSheetsQuery,
  DatabaseDownstreamSheetsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamSheetsQuery';
import { SheetsDisplayGridRowDataFragment } from '../../../gen-src/queries/SheetsDisplayGridRowDataFragment';
import { SheetSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseDownstreamSheetsFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamSheetsFilteredByTableQueryGql';
import { DatabaseDownstreamSheetsQueryGql } from '../../queries/DatabaseDownstreamSheetsQueryGql';
import { getRowDataForSheet, SheetsDisplayGrid, SheetsDisplayGridRowData } from '../sharedDisplayGrid/SheetsDisplayGrid';
import { SheetsValidSortFields } from '../sorting/sortProviders/SheetsGridSortProvider';

type QueryVariables = DatabaseDownstreamSheetsQueryVariables;
type QueryResults = DatabaseDownstreamSheetsQuery;
type QueryResultListNode = SheetsDisplayGridRowDataFragment;
type GridRowData = SheetsDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseDownstreamSheetsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamSheetsFilteredByTableQuery;
type QueryResultListNodeWithFilter = DatabaseDownstreamSheetsFilteredByTableQuery_tables_downstreamSheetsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamSheetsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamSheetsConnection;
}

export type DatabaseDownstreamSheetsQueryGridProps =
  GridSortProps<SheetsValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamSheetsQueryGrid(props: DatabaseDownstreamSheetsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamSheetsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredSheetsLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
      queryGql={DatabaseDownstreamSheetsFilteredByTableQueryGql}
      partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as SheetSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForSheet}
    >
      {({ rowStore }) => <SheetsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamSheetsQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as SheetSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForSheet}
      >
        {({ rowStore }) => <SheetsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
